import React from 'react';
import PropTypes from 'prop-types';
import t from './tokens';
import PhotoRail from './PhotoRail';

// The gallery's season chooser, mirroring the Figma Gallery: the access-code
// season is a large "NEW / View & Order" card; prior seasons are smaller
// "Reorder" cards to its right, in a horizontal carousel with scroll dots.
// Selecting a card drills into that season's poses (handled by the parent).
const s = {
  card: {
    display: 'flex',
    flexDirection: 'column',
    background: t.surface,
    borderRadius: t.radiusLg,
    overflow: 'hidden',
    boxShadow: t.shadowSm,
    border: `1px solid ${t.border}`,
    height: '100%',
  },
  cardCurrent: {
    boxShadow: t.shadowMd,
    border: `1px solid ${t.brandBorder}`,
    outline: `2px solid ${t.brandTint}`,
  },
  imgWrap: {
    position: 'relative',
    aspectRatio: '4 / 5',
    background: '#0b1b2b',
  },
  // The pose preview is a transparent cutout; the season's default background
  // shows through it (same approach as PoseCard).
  photo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    backgroundColor: '#0b1b2b',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  badge: {
    position: 'absolute',
    top: 10,
    left: 10,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: t.danger,
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 800,
    letterSpacing: '0.04em',
    padding: '0.25rem 0.6rem',
    borderRadius: t.pill,
  },
  pill: {
    position: 'absolute',
    bottom: 10,
    left: 10,
    background: t.surface,
    color: t.ink,
    fontSize: '0.72rem',
    fontWeight: 700,
    padding: '0.2rem 0.6rem',
    borderRadius: t.pill,
    boxShadow: t.shadowSm,
  },
  body: {
    padding: '0.85rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.3rem',
    flex: 1,
  },
  title: {
    fontWeight: 700, color: t.ink, fontSize: '1rem', lineHeight: 1.2,
  },
  sub: { color: t.muted, fontSize: '0.82rem' },
  status: {
    display: 'inline-flex', alignItems: 'center', gap: 5, color: t.muted, fontSize: '0.8rem',
  },
  statusDone: { color: t.success },
  btnPrimary: {
    marginTop: '0.6rem',
    width: '100%',
    background: t.brand,
    color: '#fff',
    border: 'none',
    borderRadius: t.radius,
    padding: '0.65rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
  btnReorder: {
    marginTop: '0.6rem',
    width: '100%',
    background: t.surface,
    color: t.ink,
    border: `1px solid ${t.ink}`,
    borderRadius: t.radius,
    padding: '0.65rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
  },
};

const SeasonCarousel = ({ seasons, onOpen }) => (
  <PhotoRail>
    {seasons.map((season) => {
      const { current } = season;
      const photoStyle = season.background_url
        ? { ...s.photo, backgroundImage: `url(${season.background_url})` }
        : s.photo;
      return (
        <div
          key={season.student_image_id}
          // Current season larger; prior seasons smaller (inline width beats .sg-rail > *).
          style={{ width: current ? 280 : 220 }}
        >
          <div style={current ? { ...s.card, ...s.cardCurrent } : s.card}>
            <div style={s.imgWrap}>
              {season.thumb_url && <img src={season.thumb_url} alt={season.label} style={photoStyle} />}
              {current && <span style={s.badge}>★ NEW</span>}
              {season.pose_count > 0 && (
                <span style={s.pill}>{`${season.pose_count} Pose${season.pose_count === 1 ? '' : 's'}`}</span>
              )}
            </div>
            <div style={s.body}>
              <span style={s.title}>{season.label}</span>
              <span style={s.sub}>{current ? 'Your latest session' : 'Past session'}</span>
              <span style={season.ordered ? { ...s.status, ...s.statusDone } : s.status}>
                {season.ordered ? '✓ Previously ordered' : 'Ready to order'}
              </span>
              <button
                type="button"
                // Style follows the access-code season (primary) vs prior seasons
                // (white); the label follows whether it's been ordered.
                style={current ? s.btnPrimary : s.btnReorder}
                onClick={() => onOpen(season.student_image_id)}
              >
                {season.ordered ? 'Reorder' : 'View & Order'}
              </button>
            </div>
          </div>
        </div>
      );
    })}
  </PhotoRail>
);

SeasonCarousel.propTypes = {
  seasons: PropTypes.arrayOf(PropTypes.shape({
    student_image_id: PropTypes.number,
    label: PropTypes.string,
    current: PropTypes.bool,
    thumb_url: PropTypes.string,
    background_url: PropTypes.string,
    pose_count: PropTypes.number,
    ordered: PropTypes.bool,
  })).isRequired,
  onOpen: PropTypes.func.isRequired,
};

export default SeasonCarousel;
